import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const GoalModal = ({ isOpen, onClose, onSave, editingGoal }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'car',
    targetAmount: '',
    currentSavings: '',
    targetDate: '',
    expectedReturns: '12',
    inflationRate: '6'
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editingGoal) {
      setFormData({
        name: editingGoal?.name,
        type: editingGoal?.type,
        targetAmount: editingGoal?.targetAmount?.toString(),
        currentSavings: editingGoal?.currentSavings?.toString(),
        targetDate: editingGoal?.targetDate,
        expectedReturns: editingGoal?.expectedReturns?.toString(),
        inflationRate: editingGoal?.inflationRate?.toString()
      });
    } else {
      setFormData({
        name: '',
        type: 'car',
        targetAmount: '',
        currentSavings: '',
        targetDate: '',
        expectedReturns: '12',
        inflationRate: '6'
      });
    }
    setErrors({});
  }, [editingGoal, isOpen]);

  const goalTypeOptions = [
    { value: 'car', label: 'Car Purchase', description: 'Plan for your dream vehicle' },
    { value: 'home', label: 'Home Purchase', description: 'Save for your own house' },
    { value: 'education', label: 'Kids Education', description: 'Secure your children\'s future' },
    { value: 'wedding', label: 'Wedding', description: 'Plan for wedding expenses' },
    { value: 'custom', label: 'Custom Goal', description: 'Create your own goal' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Goal name is required';
    }

    if (!formData?.targetAmount || parseFloat(formData?.targetAmount) <= 0) {
      newErrors.targetAmount = 'Please enter a valid target amount';
    }

    if (!formData?.currentSavings || parseFloat(formData?.currentSavings) < 0) {
      newErrors.currentSavings = 'Please enter current savings (0 or more)';
    }

    if (!formData?.targetDate) {
      newErrors.targetDate = 'Target date is required';
    } else {
      const targetDate = new Date(formData.targetDate);
      const today = new Date();
      if (targetDate <= today) {
        newErrors.targetDate = 'Target date must be in the future';
      }
    }

    if (!formData?.expectedReturns || parseFloat(formData?.expectedReturns) <= 0) {
      newErrors.expectedReturns = 'Please enter expected returns';
    }

    if (!formData?.inflationRate || parseFloat(formData?.inflationRate) < 0) {
      newErrors.inflationRate = 'Please enter inflation rate';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const calculateMonthlySavings = () => {
    const targetAmount = parseFloat(formData?.targetAmount);
    const currentSavings = parseFloat(formData?.currentSavings);
    const targetDate = new Date(formData.targetDate);
    const today = new Date();
    const months = (targetDate?.getFullYear() - today?.getFullYear()) * 12 + 
                   (targetDate?.getMonth() - today?.getMonth());
    
    if (months <= 0) return 0;

    const monthlyRate = parseFloat(formData?.expectedReturns) / 12 / 100;
    const inflationAdjustedTarget = targetAmount * Math.pow(1 + parseFloat(formData?.inflationRate) / 100, months / 12);
    
    const futureValueOfCurrentSavings = currentSavings * Math.pow(1 + monthlyRate, months);
    const remainingAmount = inflationAdjustedTarget - futureValueOfCurrentSavings;
    
    if (remainingAmount <= 0) return 0;
    
    const monthlySavings = (remainingAmount * monthlyRate) / (Math.pow(1 + monthlyRate, months) - 1);
    
    return Math.max(0, monthlySavings);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const goalData = {
      id: editingGoal?.id || Date.now(),
      name: formData?.name,
      type: formData?.type,
      targetAmount: parseFloat(formData?.targetAmount),
      currentSavings: parseFloat(formData?.currentSavings),
      targetDate: formData?.targetDate,
      expectedReturns: parseFloat(formData?.expectedReturns),
      inflationRate: parseFloat(formData?.inflationRate),
      monthlySavingsRequired: calculateMonthlySavings(),
      createdAt: editingGoal?.createdAt || new Date()?.toISOString()
    };

    onSave(goalData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground">
            {editingGoal ? 'Edit Goal' : 'Create New Goal'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors focus-ring"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <Input
            label="Goal Name"
            type="text"
            placeholder="e.g., New Car, Dream Home"
            value={formData?.name}
            onChange={(e) => handleChange('name', e?.target?.value)}
            error={errors?.name}
            required
          />

          <Select
            label="Goal Type"
            options={goalTypeOptions}
            value={formData?.type}
            onChange={(value) => handleChange('type', value)}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Target Amount (₹)"
              type="number"
              placeholder="e.g., 1000000"
              value={formData?.targetAmount}
              onChange={(e) => handleChange('targetAmount', e?.target?.value)}
              error={errors?.targetAmount}
              required
            />

            <Input
              label="Current Savings (₹)"
              type="number"
              placeholder="e.g., 100000"
              value={formData?.currentSavings}
              onChange={(e) => handleChange('currentSavings', e?.target?.value)}
              error={errors?.currentSavings}
              required
            />
          </div>

          <Input
            label="Target Date"
            type="date"
            value={formData?.targetDate}
            onChange={(e) => handleChange('targetDate', e?.target?.value)}
            error={errors?.targetDate}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Expected Returns (%)"
              type="number"
              placeholder="e.g., 12"
              value={formData?.expectedReturns}
              onChange={(e) => handleChange('expectedReturns', e?.target?.value)}
              error={errors?.expectedReturns}
              description="Annual return rate on investments"
              required
            />

            <Input
              label="Inflation Rate (%)"
              type="number"
              placeholder="e.g., 6"
              value={formData?.inflationRate}
              onChange={(e) => handleChange('inflationRate', e?.target?.value)}
              error={errors?.inflationRate}
              description="Expected annual inflation"
              required
            />
          </div>

          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} color="var(--color-primary)" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">Indian Context Guidance</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Car: ₹5-15 lakhs for hatchback, ₹15-30 lakhs for sedan/SUV</li>
                  <li>• Home: ₹30-80 lakhs (Tier 2), ₹80 lakhs-2 crores (Metro)</li>
                  <li>• Education: ₹20-50 lakhs for undergraduate, ₹50 lakhs-1 crore for abroad</li>
                  <li>• Wedding: ₹10-30 lakhs for traditional Indian wedding</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 pt-4">
            <Button type="button" variant="outline" fullWidth onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="default" fullWidth iconName="Save" iconPosition="left">
              {editingGoal ? 'Update Goal' : 'Create Goal'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GoalModal;